'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal, .reveal-scale, .reveal-left, .reveal-right');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const steps = [
    {
      number: '01',
      emoji: '💬',
      title: 'Describe What You Need',
      desc: 'Order marketing help as easily as hailing an Uber.',
      color: '#FF3008',
      bg: '#FFF5F5',
      delay: '0s',
    },
    {
      number: '02',
      emoji: '🧞',
      title: 'Get Paired with a Live Genie',
      desc: 'Our matching engine finds the best available expert for your specific need. Average match time: 47 seconds.',
      color: '#6C5CE7',
      bg: '#F0EDFF',
      delay: '0.15s',
    },
    {
      number: '03',
      emoji: '🚀',
      title: 'Start Working Immediately',
      desc: 'Jump into a live session with built-in chat and screen sharing. No onboarding, no waiting — just results.',
      color: '#FFB800',
      bg: '#FFFBEB',
      delay: '0.3s',
    },
  ];

  return (
    <section id="how-it-works" ref={sectionRef} className="py-20 lg:py-28 px-4 sm:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-2xl mb-16 reveal">
          <p className="text-xs font-bold uppercase tracking-widest text-muted mb-3">As Simple As Ordering a Ride</p>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
            Three steps.<br />
            <span className="text-primary">Zero friction.</span>
          </h2>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Connector line (desktop) */}
          <div className="hidden md:block absolute top-10 left-[20%] right-[20%] h-px bg-gradient-to-r from-primary via-accent to-highlight opacity-20" />

          {steps?.map((step, i) => (
            <div
              key={i}
              className="reveal-scale relative"
              style={{ transitionDelay: step?.delay }}
            >
              {/* Ghost number */}
              <span className="step-ghost-number" style={{ color: step?.color }}>{step?.number}</span>

              <div className="relative z-10 pt-10">
                {/* Step indicator */}
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 shadow-sm"
                    style={{ background: step?.bg }}
                  >
                    {step?.emoji}
                  </div>
                  <span
                    className="text-xs font-black uppercase tracking-widest"
                    style={{ color: step?.color }}
                  >
                    Step {step?.number}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-foreground mb-3 leading-tight">{step?.title}</h3>
                <p className="text-sm text-muted leading-relaxed">{step?.desc}</p>

                {/* Bottom accent line */}
                <div
                  className="mt-6 h-1 w-12 rounded-full"
                  style={{ background: step?.color }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Join Waitlist CTA after How It Works */}
        <div className="mt-16 text-center reveal" style={{ transitionDelay: '0.4s' }}>
          <Link
            href="/waitlist"
            className="inline-flex items-center justify-center gap-2 bg-primary text-white px-10 py-4 rounded-2xl font-bold text-base hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
          >
            <span>Join the Waitlist</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </Link>
          <p className="mt-3 text-sm text-muted">Be first in line when we launch. No spam, ever.</p>
        </div>
      </div>
    </section>
  );
}